import React from "react";
import type { Components } from "react-markdown";

import { CodeBlock } from "@/components/content/code-block";

const PARAGRAPH_BLOCK_CHILDREN = new Set([
  "div",
  "table",
  "ul",
  "ol",
  "pre",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);

function hasBlockChild(children: React.ReactNode): boolean {
  return React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) return false;
    return (
      typeof child.type === "string" &&
      PARAGRAPH_BLOCK_CHILDREN.has(child.type)
    );
  });
}

/**
 * The data-free subset of the shared Markdown semantics: every renderer here
 * works from its props alone, with no runtime plugin catalogue behind it.
 * `markdownRendererComponents` composes the live `a` (BrandLink, which reads
 * runtime plugin branding) on top; surfaces that render governed Markdown
 * outside the application — print, export, the marketing product bridge —
 * import this map so their module graph never reaches the app's data clients.
 */
export const staticMarkdownRendererComponents: Partial<Components> = {
  code: CodeBlock,
  pre: ({ children }) => <>{children}</>,
  p: ({ children, ...props }) => {
    // Streaming Markdown can temporarily put a block inside a paragraph.
    const Tag = hasBlockChild(children) ? "div" : "p";
    return <Tag {...props}>{children}</Tag>;
  },
  table: ({ children, ...props }) => (
    <div className="typeset-scroll">
      <table {...props}>{children}</table>
    </div>
  ),
};
